import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CircleAlertIcon, CircleCheckIcon, CircleSlashIcon, FileTextIcon, MessageSquareIcon, ShieldCheckIcon, SparklesIcon } from 'lucide-react';
import { EYEBROW } from './Band';
import { traceEvents, TraceKind } from '../../data/site';
const KIND_META: Record<TraceKind, {
  icon: typeof FileTextIcon;
  label: string;
  className: string;
}> = {
  file: {
    icon: FileTextIcon,
    label: 'Source',
    className: 'text-ink'
  },
  prompt: {
    icon: MessageSquareIcon,
    label: 'Prompt',
    className: 'text-ink'
  },
  ai: {
    icon: SparklesIcon,
    label: 'AI output',
    className: 'text-clay'
  },
  defect: {
    icon: CircleCheckIcon,
    label: 'Scenario handled',
    className: 'text-teal'
  },
  flag: {
    icon: CircleAlertIcon,
    label: 'Appropriately escalated',
    className: 'text-teal'
  },
  clean: {
    icon: ShieldCheckIcon,
    label: 'Clean case',
    className: 'text-teal'
  }
};

/** Percentage position of each event along a 90 minute session. */
function toPercent(time: string): number {
  const [h, m, s] = time.split(':').map(Number);
  return Math.min(100, (h * 3600 + m * 60 + s) / (90 * 60) * 100);
}
export function SessionTrace() {
  const [active, setActive] = useState(4);
  const event = traceEvents[active];
  const meta = KIND_META[event.kind];
  const ActiveIcon = meta.icon;
  return <section id="trace" className="w-full scroll-mt-20 border-b border-mist bg-white/60" data-id="element-369" data-mp="1a185s23kdpnv">
      <div className="mx-auto w-full max-w-[92rem] px-6 py-24 lg:px-10 lg:py-36" data-id="element-370" data-mp="152wy9y0qrr8t">
        <header className="mx-auto max-w-6xl" data-id="element-371" data-mp="103tt8dkrhl0u">
          <p className={`${EYEBROW} text-teal`} data-id="element-372" data-mp="102in473ug16k">The session trace</p>
          <h2 className="mt-5 max-w-3xl font-display text-[34px] font-light leading-[1.08] tracking-tight text-ink sm:text-5xl" data-id="element-373" data-mp="v5s6nio4n3km">
            You don't see a finished document. You see how it was made.
          </h2>
          <p className="mt-6 max-w-[68ch] text-xl leading-relaxed text-graphite" data-id="element-374" data-mp="3fugb7tfn26wn">
            Select any moment in the session. Every judgement traces to one action, one timestamp, one scenario — which is what makes it answerable eighteen months later, by someone who wasn't there.
          </p>
        </header>

        <div className="mt-14 overflow-hidden rounded-2xl border border-mist bg-white shadow-[0_30px_70px_-50px_rgba(14,36,54,0.6)]" data-id="element-375" data-mp="1xx37rb68zo2y">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-mist px-7 py-4" data-id="element-376" data-mp="1o7te3qvr9xcq">
            <span className="font-mono text-xs uppercase tracking-[0.14em] text-graphite" data-id="element-377" data-mp="1sy9rjazktiz3">
              Financial Controller · candidate 0417 · library v2026.03.4
            </span>
            <span className="flex items-center gap-2 font-mono text-xs text-teal" data-id="element-378" data-mp="1f5adzejctyu6">
              <span className="h-1.5 w-1.5 rounded-full bg-teal" data-id="element-379" data-mp="1k43rv82jzruv" />
              90:00 session · 6 scenarios
            </span>
          </div>

          {/* Timeline scrubber */}
          <div className="border-b border-mist px-8 py-10" data-id="element-380" data-mp="1a7im2kipmcpz">
            <div className="relative h-2 w-full rounded-full bg-mist" data-id="element-381" data-mp="qlp38z7tklfj">
              {traceEvents.map((item, i) => {
              const itemMeta = KIND_META[item.kind];
              const selected = i === active;
              return <button key={item.time} type="button" onClick={() => setActive(i)} onMouseEnter={() => setActive(i)} onFocus={() => setActive(i)} aria-label={`${item.time} ${item.label}`} aria-pressed={selected} style={{
                left: `${toPercent(item.time)}%`
              }} className="group absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full p-2.5" data-id="element-382" data-mp="10l7kh7wcht3w" data-mpi={i}>
                    <span className={['block rounded-full transition-all duration-200', item.weight === 'high' ? 'h-4 w-4' : 'h-3 w-3', item.kind === 'ai' ? 'bg-clay' : itemMeta.className === 'text-teal' ? 'bg-teal' : 'bg-ink/40', selected ? 'ring-[6px] ring-teal/20' : 'group-hover:ring-[6px] group-hover:ring-ink/10'].join(' ')} data-id="element-383" data-mp="1jb5rdkoepg5y" />
                    <span className="sr-only" data-id="element-384" data-mp="1obk2xcp7v88q">{itemMeta.label}</span>
                  </button>;
            })}
            </div>
            <div className="mt-5 flex justify-between font-mono text-[11px] text-graphite" data-id="element-385" data-mp="3hqvfxrgjvol9">
              <span data-id="element-386" data-mp="3mppuuibect03">00:00</span>
              <span data-id="element-387" data-mp="3risun86royoq">45:00</span>
              <span data-id="element-388" data-mp="3rju4j9c1rf4v">90:00</span>
            </div>
          </div>

          <div className="grid lg:grid-cols-[0.8fr_1.2fr]" data-id="element-389" data-mp="byee33hrnn2">
            {/* Event list */}
            <ul className="divide-y divide-mist/70 border-b border-mist lg:border-b-0 lg:border-r" data-id="element-390" data-mp="2ytvdx9i7f9y3">
              {traceEvents.map((item, i) => {
              const itemMeta = KIND_META[item.kind];
              const Icon = itemMeta.icon;
              const selected = i === active;
              return <li key={item.time} data-id="element-391" data-mp="3dr4jb74cee8t" data-mpi={i}>
                    <button type="button" onClick={() => setActive(i)} onMouseEnter={() => setActive(i)} className={`flex w-full items-start gap-3 px-7 py-5 text-left transition-colors ${selected ? 'bg-bone' : 'hover:bg-bone/60'}`} aria-current={selected} data-id="element-392" data-mp="3ds5rn3tz4mmu">
                      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${itemMeta.className}`} aria-hidden="true" data-id="element-393" data-mp="2diy0m09lrt35" />
                      <span className={`flex-1 text-[15px] leading-snug ${item.weight === 'high' ? 'font-semibold text-ink' : 'text-graphite'}`} data-id="element-394" data-mp="28k3ased7x49j">
                        {item.label}
                      </span>
                      <span className="mt-0.5 font-mono text-[11px] text-graphite" data-id="element-395" data-mp="1yujqr5drr4d3">
                        {item.time}
                      </span>
                    </button>
                  </li>;
            })}
            </ul>

            {/* Detail panel */}
            <div className="min-h-[26rem] bg-bone/50 p-8 lg:p-12" data-id="element-396" data-mp="1pvpx9nvqiu3r">
              <AnimatePresence mode="wait" data-id="element-397" data-mp="1kv1z8i939d01">
                <motion.div key={event.time} initial={{
                opacity: 0,
                y: 6
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -6
              }} transition={{
                duration: 0.2
              }} data-id="element-398" data-mp="1fv5swccx59hb">
                  <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] ${event.kind === 'ai' ? 'border-clay/30 bg-clay/5 text-clay' : meta.className === 'text-teal' ? 'border-teal/30 bg-teal/5 text-teal' : 'border-mist bg-white text-graphite'}`} data-id="element-399" data-mp="18iztaof9pc9z">
                    <ActiveIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-400" data-mp="13l7qgzd345r0" />
                    {meta.label}
                  </span>

                  <h3 className="mt-6 max-w-[24ch] font-display text-3xl font-light leading-snug text-ink" data-id="element-401" data-mp="1w9lnbbsfsw0z">
                    {event.label}
                  </h3>
                  <p className="mt-5 max-w-[62ch] text-lg leading-relaxed text-graphite" data-id="element-402" data-mp="1hc340oyj3b0x">
                    {event.detail}
                  </p>

                  <dl className="mt-9 grid gap-4 sm:grid-cols-2" data-id="element-403" data-mp="5wo7tne58n6v">
                    <div className="rounded-xl border border-mist bg-white p-5" data-id="element-404" data-mp="5zabhbxwyijt">
                      <dt className="font-mono text-[11px] uppercase tracking-[0.12em] text-graphite" data-id="element-405" data-mp="axk9dub0vx94">
                        Evidence
                      </dt>
                      <dd className="mt-2 font-mono text-sm text-ink" data-id="element-406" data-mp="2k5qei68ccxkp">
                        {event.evidence}
                      </dd>
                    </div>
                    <div className="rounded-xl border border-mist bg-white p-5" data-id="element-407" data-mp="2u34w49603sel">
                      <dt className="font-mono text-[11px] uppercase tracking-[0.12em] text-graphite" data-id="element-408" data-mp="2u10g4sutv2xy">
                        Timestamp
                      </dt>
                      <dd className="mt-2 font-mono text-sm text-ink" data-id="element-409" data-mp="21fikcdmmdhgn">
                        {event.time}
                      </dd>
                    </div>
                  </dl>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          <footer className="flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-mist bg-white px-7 py-5" data-id="element-410" data-mp="1mp1f0j6v67eh">
            <span className="flex items-center gap-2 text-sm text-graphite" data-id="element-411" data-mp="1imwpnwng7wxn">
              <CircleCheckIcon className="h-4 w-4 text-teal" aria-hidden="true" data-id="element-412" data-mp="27ja6b1u56ole" />
              Handled
            </span>
            <span className="flex items-center gap-2 text-sm text-graphite" data-id="element-413" data-mp="2civ61eh4z0z6">
              <ShieldCheckIcon className="h-4 w-4 text-teal" aria-hidden="true" data-id="element-414" data-mp="2gixa1uacg6hm" />
              Clean case, verified proportionately
            </span>
            <span className="flex items-center gap-2 text-sm text-graphite" data-id="element-415" data-mp="2ljtdk6qs9qmi">
              <CircleSlashIcon className="h-4 w-4 text-clay" aria-hidden="true" data-id="element-416" data-mp="2qgthab05uhag" />
              Accepted on trust
            </span>
            <span className="ml-auto text-sm text-graphite" data-id="element-417" data-mp="2vel8f2szdzdl">
              Decisions and evidence transitions. No attention tracking, ever.
            </span>
          </footer>
        </div>
      </div>
    </section>;
}